import { Fragment, useMemo, useState } from "react";
import { Product } from "@jasper/client-models";
import { ProductIcon } from "@jasper/forms";
import { useProductDescription } from "@jasper/product-workflows";
import { isRemovableItemType, ItemType } from "@jasper/shared";
import type { MaterialAndShadeCatalog } from "@jasper/use-cases";
import {
  ActionIcon,
  Anchor,
  Divider,
  Group,
  Stack,
  Text,
  ThemeIcon,
  UnstyledButton,
} from "@mantine/core";
import {
  IconArrowRight,
  IconPencil,
  IconPlus,
  IconTrash,
} from "@tabler/icons-react";
import { findSplintingSuggestions, SplintableCrown } from "./splinting.utils";
import { SplintingSuggestionCard } from "./SplintingSuggestionCard";

const ProductRow = ({
  product,
  materialAndShadeCatalog,
  onEdit,
  onDelete,
  onUndoSplint,
}: {
  product: Product;
  materialAndShadeCatalog: MaterialAndShadeCatalog;
  onEdit: () => void;
  onDelete: () => void;
  // Only for the crown just splinted on this page, which is highlighted
  onUndoSplint?: () => Promise<void>;
}) => {
  const { describeProduct } = useProductDescription();
  const { headline, lines } = describeProduct(product, materialAndShadeCatalog);
  const [isUndoingSplint, setIsUndoingSplint] = useState(false);

  const undoSplint = async () => {
    setIsUndoingSplint(true);
    await onUndoSplint?.().finally(() => setIsUndoingSplint(false));
  };

  return (
    <Group
      gap="sm"
      wrap="nowrap"
      py="sm"
      {...(onUndoSplint && {
        px: "sm",
        bg: "brand.0",
        style: {
          border: "1px solid var(--mantine-color-brand-2)",
          borderRadius: "var(--mantine-radius-md)",
        },
      })}
    >
      <ProductIcon
        productTypes={[product.type]}
        isRemovable={isRemovableItemType(product.type as ItemType)}
      />

      <Stack gap="s1" flex={1}>
        <Text variant="strong">{headline}</Text>
        {lines.map((line, index) => (
          <Text key={`${index}-${line}`} size="sm">
            {line}
          </Text>
        ))}
        {onUndoSplint && (
          <Anchor
            component="button"
            type="button"
            size="xs"
            fw={600}
            w="fit-content"
            disabled={isUndoingSplint}
            onClick={undoSplint}
          >
            Annuler la solidarisation
          </Anchor>
        )}
      </Stack>

      <Group gap="s1" wrap="nowrap">
        <ActionIcon
          variant="default"
          aria-label={`Modifier ${headline}`}
          onClick={onEdit}
        >
          <IconPencil size={14} />
        </ActionIcon>
        <ActionIcon
          variant="default"
          aria-label={`Supprimer ${headline}`}
          onClick={onDelete}
        >
          <IconTrash size={14} />
        </ActionIcon>
      </Group>
    </Group>
  );
};

export const ProductsSection = ({
  products,
  materialAndShadeCatalog,
  onEdit,
  onDelete,
  onAdd,
  onSplint,
  lastSplintedProductId,
  onUndoLastSplint,
}: {
  products: Product[];
  materialAndShadeCatalog: MaterialAndShadeCatalog;
  onEdit: (product: Product) => void;
  onDelete: (product: Product) => void;
  onAdd: () => void;
  onSplint: (crowns: SplintableCrown[]) => Promise<void>;
  lastSplintedProductId?: string;
  onUndoLastSplint: () => Promise<void>;
}) => {
  // Ignored suggestions stay hidden while the page is open, they come back on the next visit
  const [ignoredSuggestionKeys, setIgnoredSuggestionKeys] = useState<string[]>(
    []
  );
  const suggestions = useMemo(
    () =>
      findSplintingSuggestions(products).filter(
        suggestion => !ignoredSuggestionKeys.includes(suggestion.key)
      ),
    [products, ignoredSuggestionKeys]
  );

  return (
    <Stack gap={0}>
      {suggestions.length > 0 && (
        <Stack gap="sm" mb="sm">
          {suggestions.map(suggestion => (
            <SplintingSuggestionCard
              key={suggestion.key}
              suggestion={suggestion}
              materialAndShadeCatalog={materialAndShadeCatalog}
              onSplint={onSplint}
              onIgnore={() =>
                setIgnoredSuggestionKeys(keys => [...keys, suggestion.key])
              }
            />
          ))}
        </Stack>
      )}

      {products.map(product => (
        <Fragment key={product.id}>
          <ProductRow
            product={product}
            materialAndShadeCatalog={materialAndShadeCatalog}
            onEdit={() => onEdit(product)}
            onDelete={() => onDelete(product)}
            onUndoSplint={
              product.id === lastSplintedProductId
                ? onUndoLastSplint
                : undefined
            }
          />
          <Divider />
        </Fragment>
      ))}

      <UnstyledButton
        onClick={onAdd}
        mt="md"
        p="md"
        bg="brand.0"
        style={{
          border: "1px dashed var(--mantine-color-brand-2)",
          borderRadius: "var(--mantine-radius-md)",
        }}
      >
        <Group gap="sm" wrap="nowrap" c="brand.7">
          <ThemeIcon size="lg" radius="md" aria-hidden>
            <IconPlus size={18} />
          </ThemeIcon>
          <Text variant="strong" c="inherit" flex={1}>
            Ajouter une prothèse
          </Text>
          <IconArrowRight size={16} aria-hidden />
        </Group>
      </UnstyledButton>
    </Stack>
  );
};
